/**
 * @zh 增量序列化器
 * @en Incremental Serializer
 *
 * @zh 提供高性能的增量序列化支持，只序列化变更的数据。
 *     适用于网络同步、大场景存档、时间回溯等场景。
 * @en Provides high-performance incremental serialization, serializing only changed data.
 *     Suitable for network sync, large scene archiving, and time rewind scenarios.
 */
import { Entity } from '../Entity';
import { ComponentSerializer } from './ComponentSerializer';
import { BinarySerializer } from '../../Utils/BinarySerializer';
import { HierarchyComponent } from '../Components/HierarchyComponent';
import { HierarchySystem } from '../Systems/HierarchySystem';
// =============================================================================
// 枚举 | Enums
// =============================================================================
/**
 * @zh 变更操作类型
 * @en Change operation type
 */
export var ChangeOperation;
(function (ChangeOperation) {
    /** @zh 添加新实体 @en Entity added */
    ChangeOperation["EntityAdded"] = "entity_added";
    /** @zh 删除实体 @en Entity removed */
    ChangeOperation["EntityRemoved"] = "entity_removed";
    /** @zh 实体属性更新 @en Entity updated */
    ChangeOperation["EntityUpdated"] = "entity_updated";
    /** @zh 添加组件 @en Component added */
    ChangeOperation["ComponentAdded"] = "component_added";
    /** @zh 删除组件 @en Component removed */
    ChangeOperation["ComponentRemoved"] = "component_removed";
    /** @zh 组件数据更新 @en Component updated */
    ChangeOperation["ComponentUpdated"] = "component_updated";
    /** @zh 场景数据更新 @en Scene data updated */
    ChangeOperation["SceneDataUpdated"] = "scene_data_updated";
})(ChangeOperation || (ChangeOperation = {}));
// =============================================================================
// 常量 | Constants
// =============================================================================
const DEFAULT_OPTIONS = {
    deepComponentComparison: true,
    trackSceneData: true,
    compressSnapshot: false,
    format: 'json',
    pretty: false
};
// =============================================================================
// IncrementalSerializer
// =============================================================================
/**
 * @zh 增量序列化器类
 * @en Incremental serializer class
 *
 * @zh 提供场景快照创建、增量计算、应用和序列化功能
 * @en Provides scene snapshot creation, incremental computation, application and serialization
 */
export class IncrementalSerializer {
    // =========================================================================
    // 快照创建 | Snapshot Creation
    // =========================================================================
    /**
     * @zh 创建场景快照
     * @en Create scene snapshot
     *
     * @param scene - @zh 要快照的场景 @en Scene to snapshot
     * @param options - @zh 序列化选项 @en Serialization options
     * @returns @zh 场景快照对象 @en Scene snapshot object
     */
    static createSnapshot(scene, options) {
        const opts = { ...DEFAULT_OPTIONS, ...options };
        const snapshot = {
            version: ++this.snapshotVersion,
            entityIds: new Set(),
            entities: new Map(),
            components: new Map(),
            sceneData: new Map()
        };
        // 快照实体（支持过滤）
        for (const entity of scene.entities.buffer) {
            // 应用实体过滤器
            if (opts.entityFilter && !opts.entityFilter(entity)) {
                continue;
            }
            snapshot.entityIds.add(entity.id);
            // 获取层级信息
            const hierarchy = entity.getComponent(HierarchyComponent);
            const parentId = hierarchy?.parentId;
            // 存储实体基本信息
            snapshot.entities.set(entity.id, {
                name: entity.name,
                tag: entity.tag,
                active: entity.active,
                enabled: entity.enabled,
                updateOrder: entity.updateOrder,
                ...(parentId !== null && parentId !== undefined && { parentId })
            });
            // 快照组件
            if (opts.deepComponentComparison) {
                const componentMap = new Map();
                for (const component of entity.components) {
                    const serialized = ComponentSerializer.serialize(component);
                    if (serialized) {
                        // 使用JSON字符串存储，便于后续对比
                        componentMap.set(serialized.type, JSON.stringify(serialized.data));
                    }
                }
                if (componentMap.size > 0) {
                    snapshot.components.set(entity.id, componentMap);
                }
            }
        }
        // 快照场景数据
        if (opts.trackSceneData) {
            for (const [key, value] of scene.sceneData) {
                snapshot.sceneData.set(key, JSON.stringify(value));
            }
        }
        return snapshot;
    }
    // =========================================================================
    // 增量计算 | Incremental Computation
    // =========================================================================
    /**
     * @zh 计算增量变更
     * @en Compute incremental changes
     *
     * @param scene - @zh 当前场景 @en Current scene
     * @param baseSnapshot - @zh 基础快照 @en Base snapshot
     * @param options - @zh 序列化选项 @en Serialization options
     * @returns @zh 增量快照 @en Incremental snapshot
     */
    static computeIncremental(scene, baseSnapshot, options) {
        const opts = { ...DEFAULT_OPTIONS, ...options };
        const incremental = {
            version: ++this.snapshotVersion,
            timestamp: Date.now(),
            sceneName: scene.name,
            baseVersion: baseSnapshot.version,
            entityChanges: [],
            componentChanges: [],
            sceneDataChanges: []
        };
        const currentEntityIds = new Set();
        // 检测实体变更（支持过滤）
        for (const entity of scene.entities.buffer) {
            // 应用实体过滤器
            if (opts.entityFilter && !opts.entityFilter(entity)) {
                continue;
            }
            currentEntityIds.add(entity.id);
            // 获取层级信息
            const hierarchy = entity.getComponent(HierarchyComponent);
            const parentId = hierarchy?.parentId;
            if (!baseSnapshot.entityIds.has(entity.id)) {
                // 新增实体
                incremental.entityChanges.push({
                    operation: ChangeOperation.EntityAdded,
                    entityId: entity.id,
                    entityName: entity.name,
                    entityData: {
                        id: entity.id,
                        name: entity.name,
                        tag: entity.tag,
                        active: entity.active,
                        enabled: entity.enabled,
                        updateOrder: entity.updateOrder,
                        ...(parentId !== null && parentId !== undefined && { parentId }),
                        components: [],
                        children: []
                    }
                });
                // 新增实体的所有组件都是新增
                for (const component of entity.components) {
                    const serialized = ComponentSerializer.serialize(component);
                    if (serialized) {
                        incremental.componentChanges.push({
                            operation: ChangeOperation.ComponentAdded,
                            entityId: entity.id,
                            componentType: serialized.type,
                            componentData: serialized
                        });
                    }
                }
            }
            else {
                // 检查实体属性变更
                const oldData = baseSnapshot.entities.get(entity.id);
                const entityChanged = oldData.name !== entity.name ||
                    oldData.tag !== entity.tag ||
                    oldData.active !== entity.active ||
                    oldData.enabled !== entity.enabled ||
                    oldData.updateOrder !== entity.updateOrder ||
                    oldData.parentId !== parentId;
                if (entityChanged) {
                    incremental.entityChanges.push({
                        operation: ChangeOperation.EntityUpdated,
                        entityId: entity.id,
                        entityData: {
                            name: entity.name,
                            tag: entity.tag,
                            active: entity.active,
                            enabled: entity.enabled,
                            updateOrder: entity.updateOrder,
                            ...(parentId !== null && parentId !== undefined && { parentId })
                        }
                    });
                }
                // 检查组件变更
                if (opts.deepComponentComparison) {
                    this.detectComponentChanges(entity, baseSnapshot, incremental.componentChanges);
                }
            }
        }
        // 检测删除的实体
        for (const oldEntityId of baseSnapshot.entityIds) {
            if (!currentEntityIds.has(oldEntityId)) {
                incremental.entityChanges.push({
                    operation: ChangeOperation.EntityRemoved,
                    entityId: oldEntityId
                });
            }
        }
        // 检测场景数据变更
        if (opts.trackSceneData) {
            this.detectSceneDataChanges(scene, baseSnapshot, incremental.sceneDataChanges);
        }
        return incremental;
    }
    // =========================================================================
    // 私有方法 - 变更检测 | Private Methods - Change Detection
    // =========================================================================
    /**
     * @zh 检测组件变更
     * @en Detect component changes
     */
    static detectComponentChanges(entity, baseSnapshot, componentChanges) {
        const oldComponents = baseSnapshot.components.get(entity.id);
        const currentComponents = new Map();
        // 收集当前组件
        for (const component of entity.components) {
            const serialized = ComponentSerializer.serialize(component);
            if (serialized) {
                currentComponents.set(serialized.type, serialized);
            }
        }
        // 检测新增和更新的组件
        for (const [type, serialized] of currentComponents) {
            const currentData = JSON.stringify(serialized.data);
            if (!oldComponents || !oldComponents.has(type)) {
                // 新增组件
                componentChanges.push({
                    operation: ChangeOperation.ComponentAdded,
                    entityId: entity.id,
                    componentType: type,
                    componentData: serialized
                });
            }
            else if (oldComponents.get(type) !== currentData) {
                // 组件数据变更
                componentChanges.push({
                    operation: ChangeOperation.ComponentUpdated,
                    entityId: entity.id,
                    componentType: type,
                    componentData: serialized
                });
            }
        }
        // 检测删除的组件
        if (oldComponents) {
            for (const oldType of oldComponents.keys()) {
                if (!currentComponents.has(oldType)) {
                    componentChanges.push({
                        operation: ChangeOperation.ComponentRemoved,
                        entityId: entity.id,
                        componentType: oldType
                    });
                }
            }
        }
    }
    /**
     * @zh 检测场景数据变更
     * @en Detect scene data changes
     */
    static detectSceneDataChanges(scene, baseSnapshot, sceneDataChanges) {
        const currentKeys = new Set();
        // 检测新增和更新的场景数据
        for (const [key, value] of scene.sceneData) {
            currentKeys.add(key);
            const currentValue = JSON.stringify(value);
            const oldValue = baseSnapshot.sceneData.get(key);
            if (!oldValue || oldValue !== currentValue) {
                sceneDataChanges.push({
                    operation: ChangeOperation.SceneDataUpdated,
                    key,
                    value
                });
            }
        }
        // 检测删除的场景数据
        for (const oldKey of baseSnapshot.sceneData.keys()) {
            if (!currentKeys.has(oldKey)) {
                sceneDataChanges.push({
                    operation: ChangeOperation.SceneDataUpdated,
                    key: oldKey,
                    value: undefined,
                    deleted: true
                });
            }
        }
    }
    // =========================================================================
    // 增量应用 | Incremental Application
    // =========================================================================
    /**
     * @zh 应用增量变更到场景
     * @en Apply incremental changes to scene
     *
     * @param scene - @zh 目标场景 @en Target scene
     * @param incremental - @zh 增量快照 @en Incremental snapshot
     * @param componentRegistry - @zh 组件类型注册表 @en Component type registry
     */
    static applyIncremental(scene, incremental, componentRegistry) {
        // 应用实体变更
        for (const change of incremental.entityChanges) {
            switch (change.operation) {
                case ChangeOperation.EntityAdded:
                    this.applyEntityAdded(scene, change);
                    break;
                case ChangeOperation.EntityRemoved:
                    this.applyEntityRemoved(scene, change);
                    break;
                case ChangeOperation.EntityUpdated:
                    this.applyEntityUpdated(scene, change);
                    break;
            }
        }
        // 应用组件变更
        for (const change of incremental.componentChanges) {
            switch (change.operation) {
                case ChangeOperation.ComponentAdded:
                    this.applyComponentAdded(scene, change, componentRegistry);
                    break;
                case ChangeOperation.ComponentRemoved:
                    this.applyComponentRemoved(scene, change, componentRegistry);
                    break;
                case ChangeOperation.ComponentUpdated:
                    this.applyComponentUpdated(scene, change, componentRegistry);
                    break;
            }
        }
        // 应用场景数据变更
        for (const change of incremental.sceneDataChanges) {
            if (change.deleted) {
                scene.sceneData.delete(change.key);
            }
            else {
                scene.sceneData.set(change.key, change.value);
            }
        }
    }
    static applyEntityAdded(scene, change) {
        if (!change.entityData)
            return;
        const entity = new Entity(change.entityName || 'Entity', change.entityId);
        entity.tag = change.entityData.tag || 0;
        entity.active = change.entityData.active ?? true;
        entity.enabled = change.entityData.enabled ?? true;
        entity.updateOrder = change.entityData.updateOrder || 0;
        scene.addEntity(entity);
    }
    static applyEntityRemoved(scene, change) {
        const entity = scene.entities.findEntityById(change.entityId);
        if (entity) {
            entity.destroy();
        }
    }
    static applyEntityUpdated(scene, change) {
        if (!change.entityData)
            return;
        const entity = scene.entities.findEntityById(change.entityId);
        if (!entity)
            return;
        if (change.entityData.name !== undefined)
            entity.name = change.entityData.name;
        if (change.entityData.tag !== undefined)
            entity.tag = change.entityData.tag;
        if (change.entityData.active !== undefined)
            entity.active = change.entityData.active;
        if (change.entityData.enabled !== undefined)
            entity.enabled = change.entityData.enabled;
        if (change.entityData.updateOrder !== undefined)
            entity.updateOrder = change.entityData.updateOrder;
        // 使用 HierarchySystem 更新层级关系
        const hierarchySystem = scene.getSystem(HierarchySystem);
        if (hierarchySystem) {
            const hierarchy = entity.getComponent(HierarchyComponent);
            const currentParentId = hierarchy?.parentId;
            if (change.entityData.parentId !== undefined) {
                const newParent = scene.entities.findEntityById(change.entityData.parentId);
                if (newParent && currentParentId !== change.entityData.parentId) {
                    hierarchySystem.setParent(entity, newParent);
                }
            }
            else if (currentParentId !== null && currentParentId !== undefined) {
                hierarchySystem.setParent(entity, null);
            }
        }
    }
    static applyComponentAdded(scene, change, componentRegistry) {
        if (!change.componentData)
            return;
        const entity = scene.entities.findEntityById(change.entityId);
        if (!entity)
            return;
        const component = ComponentSerializer.deserialize(change.componentData, componentRegistry);
        if (component) {
            entity.addComponent(component);
        }
    }
    static applyComponentRemoved(scene, change, componentRegistry) {
        const entity = scene.entities.findEntityById(change.entityId);
        if (!entity)
            return;
        const componentClass = componentRegistry.get(change.componentType);
        if (!componentClass)
            return;
        entity.removeComponentByType(componentClass);
    }
    static applyComponentUpdated(scene, change, componentRegistry) {
        if (!change.componentData)
            return;
        const entity = scene.entities.findEntityById(change.entityId);
        if (!entity)
            return;
        const componentClass = componentRegistry.get(change.componentType);
        if (!componentClass)
            return;
        entity.removeComponentByType(componentClass);
        const component = ComponentSerializer.deserialize(change.componentData, componentRegistry);
        if (component) {
            entity.addComponent(component);
        }
    }
    // =========================================================================
    // 序列化与反序列化 | Serialization & Deserialization
    // =========================================================================
    /**
     * @zh 序列化增量快照
     * @en Serialize incremental snapshot
     *
     * @param incremental - @zh 增量快照 @en Incremental snapshot
     * @param options - @zh 序列化选项 @en Serialization options
     * @returns @zh 序列化后的数据 @en Serialized data
     *
     * @example
     * ```typescript
     * // JSON格式（默认）
     * const jsonData = IncrementalSerializer.serializeIncremental(snapshot);
     *
     * // 二进制格式
     * const binaryData = IncrementalSerializer.serializeIncremental(snapshot, {
     *     format: 'binary'
     * });
     * ```
     */
    static serializeIncremental(incremental, options) {
        const format = options?.format ?? 'json';
        const pretty = options?.pretty ?? false;
        if (format === 'binary') {
            return BinarySerializer.encode(incremental);
        }
        return pretty ? JSON.stringify(incremental, null, 2) : JSON.stringify(incremental);
    }
    /**
     * @zh 反序列化增量快照
     * @en Deserialize incremental snapshot
     *
     * @param data - @zh 序列化的数据 @en Serialized data
     * @returns @zh 增量快照 @en Incremental snapshot
     */
    static deserializeIncremental(data) {
        if (typeof data === 'string') {
            return JSON.parse(data);
        }
        return BinarySerializer.decode(data);
    }
    // =========================================================================
    // 统计与工具 | Statistics & Utilities
    // =========================================================================
    /**
     * @zh 获取增量快照的统计信息
     * @en Get incremental snapshot statistics
     *
     * @param incremental - @zh 增量快照 @en Incremental snapshot
     * @returns @zh 统计信息 @en Statistics
     */
    static getIncrementalStats(incremental) {
        const entityStats = { added: 0, removed: 0, updated: 0 };
        const componentStats = { added: 0, removed: 0, updated: 0 };
        for (const change of incremental.entityChanges) {
            if (change.operation === ChangeOperation.EntityAdded)
                entityStats.added++;
            else if (change.operation === ChangeOperation.EntityRemoved)
                entityStats.removed++;
            else if (change.operation === ChangeOperation.EntityUpdated)
                entityStats.updated++;
        }
        for (const change of incremental.componentChanges) {
            if (change.operation === ChangeOperation.ComponentAdded)
                componentStats.added++;
            else if (change.operation === ChangeOperation.ComponentRemoved)
                componentStats.removed++;
            else if (change.operation === ChangeOperation.ComponentUpdated)
                componentStats.updated++;
        }
        return {
            totalChanges: incremental.entityChanges.length +
                incremental.componentChanges.length +
                incremental.sceneDataChanges.length,
            entityChanges: incremental.entityChanges.length,
            componentChanges: incremental.componentChanges.length,
            sceneDataChanges: incremental.sceneDataChanges.length,
            addedEntities: entityStats.added,
            removedEntities: entityStats.removed,
            updatedEntities: entityStats.updated,
            addedComponents: componentStats.added,
            removedComponents: componentStats.removed,
            updatedComponents: componentStats.updated
        };
    }
    /**
     * @zh 重置快照版本号（用于测试）
     * @en Reset snapshot version (for testing)
     */
    static resetVersion() {
        this.snapshotVersion = 0;
    }
}
/** @zh 当前快照版本号 @en Current snapshot version */
IncrementalSerializer.snapshotVersion = 0;
//# sourceMappingURL=IncrementalSerializer.js.map